import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { Calculator, Sparkles, TrendingUp, Coins, Zap, ArrowRight, Crown } from 'lucide-react';

/**
 * RoiCalculator — viral homepage widget that demonstrates how
 * combining a Mythic NFT Booster Pack + B4P currency outperforms
 * any other path on Bet4Peer.
 *
 * Hard-coded math (matches backend currencies_service + booster_packs):
 *   Without Mythic + USDC : 3% fee, ×1 XP, ×1 jackpot tickets, base staking APY
 *   With Mythic    + B4P  : 1.8% fee (2% × 0.9), ×2 XP, ×4 tickets (×2 currency × ×2 pack), +30% staking APY
 */
export default function RoiCalculator() {
    const { t } = useTranslation();
    const [betSize, setBetSize] = useState(1000);              // amount per bet
    const [betsPerWeek, setBetsPerWeek] = useState(5);
    const [stakingAmount, setStakingAmount] = useState(5000);  // staked B4P / equivalent

    const result = useMemo(() => {
        // 1 year volume
        const yearlyVolume = betSize * betsPerWeek * 52;

        // Fee saved (the bonus the platform "gives back" to the user via reduced fee)
        const noPackFeePct = 3.0;
        const mythicB4pFeePct = 1.8;  // 2% × (1 - 10% Mythic discount)
        const feeSaved = yearlyVolume * (noPackFeePct - mythicB4pFeePct) / 100;

        // XP/quests bonus (×2 currency × ×2 reward Mythic-equivalent boost = ×4 vs base)
        // Conservative: XP value = 0.5% of volume per year
        const baseXp = yearlyVolume * 0.005;
        const boostedXp = baseXp * 2;  // ×2 from B4P currency reward_multiplier
        const xpExtra = boostedXp - baseXp;

        // Jackpot tickets — Mythic ×2 × B4P ×2 = ×4. Conservative jackpot edge per year ~ 1% of volume
        const jackpotEdgeBase = yearlyVolume * 0.005;
        const jackpotEdgeBoosted = jackpotEdgeBase * 4;
        const jackpotExtra = jackpotEdgeBoosted - jackpotEdgeBase;

        // Staking APY: 200% lock 365d. Mythic gives +30% on APY (relative).
        // base = stakingAmount × 200%, boosted = × 260%
        const baseApyPct = 200;
        const boostedApyPct = 260;
        const baseStakingYearly = stakingAmount * (baseApyPct / 100);
        const boostedStakingYearly = stakingAmount * (boostedApyPct / 100);
        const stakingExtra = boostedStakingYearly - baseStakingYearly;

        const totalExtra = feeSaved + xpExtra + jackpotExtra + stakingExtra;
        const mythicCost = 500;  // $
        const roiMultiple = totalExtra / mythicCost;

        return {
            yearlyVolume,
            feeSaved,
            xpExtra,
            jackpotExtra,
            stakingExtra,
            totalExtra,
            mythicCost,
            roiMultiple,
            withoutTotal: noPackFeePct * yearlyVolume / 100,  // what they pay in fees w/o pack
            withTotal: mythicB4pFeePct * yearlyVolume / 100,
        };
    }, [betSize, betsPerWeek, stakingAmount]);

    return (
        <section
            className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16"
            data-testid="roi-calculator-section"
        >
            <div className="text-center mb-8 sm:mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFD800]/15 border border-[#FFD800]/30 mb-4">
                    <Calculator className="w-4 h-4 text-[#FFD800]" />
                    <span className="text-xs font-bold text-[#FFD800] tracking-wider">{t('roi.badge')}</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3">
                    {t('roi.title')} <span className="text-[#FFD800]">{t('roi.titleHighlight')}</span> {t('roi.titleWith')} <span className="text-[#FFD800]">{t('roi.titleWithHighlight')}</span>{t('roi.titleSuffix')}
                </h2>
                <p className="text-white/60 max-w-2xl mx-auto text-sm sm:text-base">
                    {t('roi.description')}
                </p>
            </div>

            <Card className="bg-gradient-to-br from-[#003B71]/95 via-[#002A52]/95 to-[#003B71]/95 border-[#FFD800]/40 backdrop-blur-sm shadow-2xl overflow-hidden">
                <CardContent className="p-6 sm:p-8">
                    <div className="grid lg:grid-cols-2 gap-8">
                        {/* INPUTS */}
                        <div className="space-y-6" data-testid="roi-inputs">
                            <h3 className="text-white font-bold text-lg flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-[#FFD800]" />
                                {t('roi.yourParams')}
                            </h3>

                            {/* Bet size */}
                            <div>
                                <div className="flex justify-between items-baseline mb-2">
                                    <label className="text-sm text-white/70 font-medium">{t('roi.betSize')}</label>
                                    <span className="text-[#FFD800] font-mono font-bold text-xl">{betSize.toLocaleString()} <span className="text-xs">B4P</span></span>
                                </div>
                                <input
                                    type="range" min="100" max="100000" step="100"
                                    value={betSize}
                                    onChange={e => setBetSize(parseInt(e.target.value))}
                                    className="w-full accent-[#FFD800]"
                                    data-testid="roi-bet-size"
                                />
                                <div className="flex justify-between text-[10px] text-white/40 mt-1">
                                    <span>100</span>
                                    <span>100k</span>
                                </div>
                            </div>

                            {/* Bets per week */}
                            <div>
                                <div className="flex justify-between items-baseline mb-2">
                                    <label className="text-sm text-white/70 font-medium">{t('roi.betsPerWeek')}</label>
                                    <span className="text-[#FFD800] font-mono font-bold text-xl">{betsPerWeek}</span>
                                </div>
                                <input
                                    type="range" min="1" max="50" step="1"
                                    value={betsPerWeek}
                                    onChange={e => setBetsPerWeek(parseInt(e.target.value))}
                                    className="w-full accent-[#FFD800]"
                                    data-testid="roi-bets-per-week"
                                />
                                <div className="flex justify-between text-[10px] text-white/40 mt-1">
                                    <span>1</span>
                                    <span>50</span>
                                </div>
                            </div>

                            {/* Staking */}
                            <div>
                                <div className="flex justify-between items-baseline mb-2">
                                    <label className="text-sm text-white/70 font-medium">{t('roi.stakingAmount')}</label>
                                    <span className="text-[#FFD800] font-mono font-bold text-xl">{stakingAmount.toLocaleString()} <span className="text-xs">B4P</span></span>
                                </div>
                                <input
                                    type="range" min="0" max="200000" step="500"
                                    value={stakingAmount}
                                    onChange={e => setStakingAmount(parseInt(e.target.value))}
                                    className="w-full accent-[#FFD800]"
                                    data-testid="roi-staking-amount"
                                />
                                <div className="flex justify-between text-[10px] text-white/40 mt-1">
                                    <span>0</span>
                                    <span>200k</span>
                                </div>
                            </div>

                            {/* Volume résumé */}
                            <div className="pt-4 border-t border-white/10 text-xs text-white/60">
                                {t('roi.yearlyVolume')} : <span className="text-white font-bold">{Math.round(result.yearlyVolume).toLocaleString()} B4P</span>
                            </div>
                        </div>

                        {/* RESULTS */}
                        <div className="relative" data-testid="roi-results">
                            <div className="absolute inset-0 bg-gradient-radial from-[#FFD800]/10 via-transparent to-transparent blur-2xl pointer-events-none" />

                            <div className="relative bg-white/5 border border-[#FFD800]/30 rounded-2xl p-5 sm:p-6 space-y-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <Crown className="w-5 h-5 text-[#FFD800]" />
                                    <h3 className="text-white font-bold">{t('roi.yourGains')}</h3>
                                </div>

                                <Row icon={Coins} label={t('roi.feeSaved')} value={result.feeSaved} testId="roi-fee-saved" />
                                <Row icon={Zap} label={t('roi.xpExtra')} value={result.xpExtra} testId="roi-xp-extra" />
                                <Row icon={Sparkles} label={t('roi.jackpotExtra')} value={result.jackpotExtra} testId="roi-jackpot-extra" />
                                <Row icon={TrendingUp} label={t('roi.stakingExtra')} value={result.stakingExtra} testId="roi-staking-extra" />

                                <div className="pt-4 border-t border-[#FFD800]/30">
                                    <div className="flex justify-between items-baseline">
                                        <span className="text-white/80 font-bold">{t('roi.totalBonus')}</span>
                                        <span className="text-3xl font-black text-[#FFD800]" data-testid="roi-total-extra">
                                            +{Math.round(result.totalExtra).toLocaleString()} B4P
                                        </span>
                                    </div>
                                    <div className="mt-2 flex justify-between items-baseline text-sm">
                                        <span className="text-white/50">{t('roi.mythicCost')}</span>
                                        <span className="text-white/80 font-mono">${result.mythicCost}</span>
                                    </div>
                                    <div className="mt-1 flex justify-between items-baseline">
                                        <span className="text-white/70 font-medium">{t('roi.estimatedRoi')}</span>
                                        <span
                                            className="text-2xl font-black"
                                            style={{ color: result.roiMultiple >= 5 ? '#00FF66' : result.roiMultiple >= 1 ? '#FFD800' : '#E5E7EB' }}
                                            data-testid="roi-multiple"
                                        >
                                            ×{result.roiMultiple.toFixed(1)}
                                        </span>
                                    </div>
                                </div>

                                {/* CTAs */}
                                <div className="grid grid-cols-2 gap-2 pt-4">
                                    <Link to="/booster-packs" className="block" data-testid="roi-cta-mythic">
                                        <Button className="w-full bg-[#FFD800] text-[#003B71] hover:bg-[#FFC800] font-bold shadow-lg">
                                            <Crown className="w-4 h-4 mr-1.5" />
                                            {t('roi.buyMythic')}
                                            <ArrowRight className="w-4 h-4 ml-1.5" />
                                        </Button>
                                    </Link>
                                    <Link to="/b4p" className="block" data-testid="roi-cta-b4p">
                                        <Button variant="outline" className="w-full border-[#FFD800]/50 text-[#FFD800] hover:bg-[#FFD800]/15 font-bold">
                                            <Coins className="w-4 h-4 mr-1.5" />
                                            {t('roi.b4pToken')}
                                        </Button>
                                    </Link>
                                </div>
                            </div>

                            <p className="text-[10px] text-white/40 text-center mt-3 leading-relaxed">
                                {t('roi.disclaimer')}
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </section>
    );
}

const Row = ({ icon: Icon, label, value, testId }) => (
    <div className="flex items-center justify-between text-sm" data-testid={testId}>
        <div className="flex items-center gap-2 text-white/70">
            <Icon className="w-4 h-4 text-[#FFD800]" />
            <span>{label}</span>
        </div>
        <span className="font-mono font-bold text-white">
            +{Math.round(value).toLocaleString()} <span className="text-[10px] text-white/50">B4P</span>
        </span>
    </div>
);
